// Single responsibility: hold pure helper functions for dashboard search processing.
import {
  SCORE_WEIGHTS,
  SEARCH_ACTION_SCORE_THRESHOLD,
  SEARCH_DEFAULT_ACTION_SCORE,
  SEARCH_SCORE_THRESHOLD,
} from "./searchConstants";

/**
 * Normalizes any input into a lowercased, trimmed, single-spaced string.
 * @param {string | null | undefined} str Raw input text.
 * @returns {string} Normalized text, or an empty string for nullish input.
 */
export function normalizeString(str) {
  return String(str || "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Splits text into normalized non-empty word tokens.
 * @param {string | null | undefined} str Raw input text.
 * @returns {string[]} Token list.
 */
export function tokenize(str) {
  return normalizeString(str).split(" ").filter(Boolean);
}

/**
 * Scores an exact text match.
 * @param {string} query Search query.
 * @param {string} target Target text.
 * @returns {number} Exact-match score or 0.
 */
export function scoreExactMatch(query, target) {
  return normalizeString(query) === normalizeString(target) && normalizeString(query)
    ? SCORE_WEIGHTS.exactMatch
    : 0;
}

/**
 * Scores when the target starts with the query.
 * @param {string} query Search query.
 * @param {string} target Target text.
 * @returns {number} Prefix-match score or 0.
 */
export function scorePrefixMatch(query, target) {
  const normalizedQuery = normalizeString(query);
  const normalizedTarget = normalizeString(target);
  return normalizedQuery && normalizedTarget.startsWith(normalizedQuery) ? SCORE_WEIGHTS.prefixMatch : 0;
}

/**
 * Scores fuzzy token overlap between query and target.
 * @param {string} query Search query.
 * @param {string} target Target text.
 * @returns {number} Fuzzy-match score or 0.
 */
export function scoreFuzzyMatch(query, target) {
  const normalizedTarget = normalizeString(target);
  const queryTokens = tokenize(query);
  if (!normalizedTarget || queryTokens.length === 0) return 0;

  let score = 0;
  for (const queryToken of queryTokens) {
    if (normalizedTarget.startsWith(queryToken)) score += 28;
    else if (normalizedTarget.includes(queryToken)) score += 18;
    else score -= 8;
  }
  const offset = normalizedTarget.indexOf(normalizeString(query));
  if (offset >= 0) score += SCORE_WEIGHTS.fuzzyMatch - Math.min(60, offset * 2);
  return Math.max(0, score);
}

/**
 * Scores compact subsequence matches for typo-tolerant ranking.
 * @param {string} target Target text.
 * @param {string} query Search query.
 * @returns {number} Subsequence score.
 */
function scoreSubsequenceMatch(target, query) {
  if (!target || !query) return 0;
  let targetIndex = 0;
  let queryIndex = 0;
  let gaps = 0;
  while (targetIndex < target.length && queryIndex < query.length) {
    if (target[targetIndex] === query[queryIndex]) queryIndex += 1;
    else gaps += 1;
    targetIndex += 1;
  }
  if (queryIndex !== query.length) return 0;
  return Math.max(1, 26 - Math.min(25, gaps));
}

/**
 * Computes bounded edit distance for short typo correction boosts.
 * @param {string} sourceText Source text.
 * @param {string} targetText Target text.
 * @param {number} maxDistance Max edit distance to evaluate.
 * @returns {number} Edit distance, or maxDistance + 1 when above bound.
 */
function computeBoundedEditDistance(sourceText, targetText, maxDistance = 2) {
  if (Math.abs(sourceText.length - targetText.length) > maxDistance) return maxDistance + 1;
  const previousRow = Array.from({ length: targetText.length + 1 }, (_, index) => index);
  for (let sourceIndex = 1; sourceIndex <= sourceText.length; sourceIndex += 1) {
    const currentRow = [sourceIndex];
    let minimumDistance = currentRow[0];
    for (let targetIndex = 1; targetIndex <= targetText.length; targetIndex += 1) {
      const substitutionCost = sourceText[sourceIndex - 1] === targetText[targetIndex - 1] ? 0 : 1;
      const nextDistance = Math.min(
        previousRow[targetIndex] + 1,
        currentRow[targetIndex - 1] + 1,
        previousRow[targetIndex - 1] + substitutionCost,
      );
      currentRow.push(nextDistance);
      minimumDistance = Math.min(minimumDistance, nextDistance);
    }
    if (minimumDistance > maxDistance) return maxDistance + 1;
    for (let targetIndex = 0; targetIndex < currentRow.length; targetIndex += 1) {
      previousRow[targetIndex] = currentRow[targetIndex];
    }
  }
  return previousRow[targetText.length];
}

/**
 * Scores one medication against a query using weighted composite rules.
 * @param {string} query Search query.
 * @param {Record<string, any>} medicationObject Medication data object.
 * @returns {number} Composite score or 0 below threshold.
 */
export function scoreMedicationItem(query, medicationObject) {
  const normalizedQuery = normalizeString(query);
  if (!normalizedQuery) return 0;

  const name = normalizeString(medicationObject?.name);
  const dosage = normalizeString(medicationObject?.dosage);
  const unit = normalizeString(medicationObject?.unit);
  const category = normalizeString(medicationObject?.productCategory);
  const status =
    medicationObject?.status === "item_expired"
      ? "expired"
      : medicationObject?.status === "item_moved"
        ? "moved"
        : "active";

  const searchableText = [name, dosage, unit, category, status, "inventory line"].join(" ");
  const normalizedSearchableText = normalizeString(searchableText);
  if (!normalizedSearchableText) return 0;

  let score = 0;
  score += scoreExactMatch(normalizedQuery, normalizedSearchableText);
  score += scorePrefixMatch(normalizedQuery, normalizedSearchableText);
  score += scoreFuzzyMatch(normalizedQuery, normalizedSearchableText);
  score += scoreSubsequenceMatch(normalizedSearchableText, normalizedQuery);

  if (normalizedQuery.length >= 3 && normalizedQuery.length <= 24) {
    const editDistance = computeBoundedEditDistance(
      normalizedSearchableText.slice(0, Math.max(normalizedQuery.length, 8)),
      normalizedQuery,
      2,
    );
    if (editDistance <= 2) score += (3 - editDistance) * 10;
  }

  score += scoreExactMatch(normalizedQuery, name) * SCORE_WEIGHTS.nameField;
  score += scorePrefixMatch(normalizedQuery, name) * SCORE_WEIGHTS.nameField;
  score += scoreFuzzyMatch(normalizedQuery, name) * SCORE_WEIGHTS.nameField;
  score += scoreExactMatch(normalizedQuery, dosage) * SCORE_WEIGHTS.batchField;
  score += scorePrefixMatch(normalizedQuery, dosage) * SCORE_WEIGHTS.batchField;

  return score > SEARCH_SCORE_THRESHOLD ? score : 0;
}

/**
 * Scores one action item against a query.
 * @param {string} query Search query.
 * @param {{ title?: string; subtitle?: string; keywords?: string }} actionItem Action definition.
 * @returns {number} Action score.
 */
export function scoreActionItem(query, actionItem) {
  const normalizedQuery = normalizeString(query);
  if (!normalizedQuery) return SEARCH_DEFAULT_ACTION_SCORE;
  const targetText = `${actionItem?.title || ""} ${actionItem?.subtitle || ""} ${actionItem?.keywords || ""}`;
  const score = scoreExactMatch(normalizedQuery, targetText) + scorePrefixMatch(normalizedQuery, targetText) + scoreFuzzyMatch(normalizedQuery, targetText);
  return score > SEARCH_ACTION_SCORE_THRESHOLD ? score : 0;
}

/**
 * Stably sorts score objects descending by score.
 * @template T
 * @param {{ item: T; score: number }[]} scoredItems List of scored entries.
 * @returns {{ item: T; score: number }[]} Sorted entries.
 */
export function sortByScore(scoredItems) {
  return scoredItems
    .map((scoredItem, index) => ({ ...scoredItem, stableIndex: index }))
    .sort((leftScoredItem, rightScoredItem) => {
      if (rightScoredItem.score !== leftScoredItem.score) {
        return rightScoredItem.score - leftScoredItem.score;
      }
      return leftScoredItem.stableIndex - rightScoredItem.stableIndex;
    })
    .map((entry) => ({ item: entry.item, score: entry.score }));
}
